'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { Button } from '@tailark/core/ui/button'
import { Card, CardContent } from '@tailark/core/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@tailark/core/ui/tabs'
import { AreaChart, ArrowUpRightIcon, Globe, Heart, Lightbulb, ShieldCheck, Users, Zap, BarChart3 } from 'lucide-react'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import CallToAction from './call-to-action'

// Types
type CompanyValue = {
    id: string
    name: string
    description: string
    icon: React.ElementType
    color: string
    principles: string[]
    testimonial?: {
        quote: string
        author: string
        role: string
        image: string
    }
    image?: string
}

// Lummeny company values data
const companyValues: CompanyValue[] = [
    {
        id: 'simplicity',
        name: 'Simplicity',
        description: 'We believe that powerful tools should be accessible to everyone. Our no-code platform eliminates the complexity of traditional development, allowing anyone to create professional forms and pages in minutes.',
        icon: Zap,
        color: 'text-amber-500',
        principles: ['Design intuitive interfaces that anyone can use', 'Eliminate unnecessary complexity in our tools', 'Provide clear guidance and helpful templates', 'Make powerful features accessible without coding'],
        testimonial: {
            quote: 'Before Lummeny, I was spending weeks working with developers to create simple forms. Now I can build beautiful, functional pages in minutes. The simplicity of the platform is what makes it so powerful.',
            author: 'Maria Rodriguez',
            role: 'Marketing Director',
            image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=400',
        },
        image: 'https://images.unsplash.com/photo-1534120247760-c44c3e4a62f1?q=80&w=800',
    },
    {
        id: 'empowerment',
        name: 'Empowerment',
        description: 'We empower businesses of all sizes to take control of their digital presence. From startups to enterprises, our platform gives teams the tools they need to create, iterate, and succeed without technical barriers.',
        icon: Users,
        color: 'text-indigo-500',
        principles: ['Enable non-technical teams to build professional solutions', 'Provide the tools needed for business growth', 'Support teams in taking ownership of their digital assets', 'Break down barriers between ideas and execution'],
        testimonial: {
            quote: "Lummeny has transformed how our marketing team works. We no longer need to wait for IT to create forms or landing pages. We can test ideas quickly and iterate based on real feedback. It's given us the freedom to be more creative and responsive.",
            author: 'David Chen',
            role: 'Growth Marketing Manager',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400',
        },
        image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=800',
    },
    {
        id: 'innovation',
        name: 'Innovation',
        description: "We constantly push the boundaries of what's possible in no-code development. Our platform combines cutting-edge technology with user-friendly design to deliver solutions that exceed expectations.",
        icon: Lightbulb,
        color: 'text-yellow-500',
        principles: ['Continuously improve our drag-and-drop technology', 'Explore new ways to simplify complex workflows', 'Integrate with the latest tools and platforms', 'Stay ahead of industry trends and user needs'],
        testimonial: {
            quote: "What impresses me most about Lummeny is how they keep innovating while maintaining simplicity. Every new feature feels intuitive and immediately useful. They're not just following trends—they're setting them.",
            author: 'Sarah Johnson',
            role: 'Product Manager',
            image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?q=80&w=400',
        },
        image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=800',
    },
    {
        id: 'quality',
        name: 'Quality',
        description: 'We maintain the highest standards in everything we build. From our user interface to the forms and pages our users create, quality is non-negotiable. We believe that professional results should be achievable by everyone.',
        icon: ShieldCheck,
        color: 'text-blue-500',
        principles: ['Ensure every template meets professional standards', 'Maintain consistent performance and reliability', 'Provide comprehensive customization options', 'Deliver pixel-perfect results across all devices'],
        testimonial: {
            quote: 'The quality of forms I can create with Lummeny rivals what our development team used to build. The attention to detail in the templates and the flexibility of the customization options is outstanding.',
            author: 'Alex Thompson',
            role: 'UX Designer',
            image: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?q=80&w=400',
        },
        image: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?q=80&w=800',
    },
    {
        id: 'customer-success',
        name: 'Customer Success',
        description: "Your success is our success. We're committed to helping you achieve your goals, whether that's generating more leads, collecting feedback, or streamlining your business processes. We measure our success by yours.",
        icon: Heart,
        color: 'text-red-500',
        principles: ['Understand and solve real business problems', 'Provide exceptional support and guidance', 'Help customers achieve measurable results', 'Build long-term partnerships based on mutual success'],
        testimonial: {
            quote: "The Lummeny team doesn't just provide a tool—they partner with us to understand our business needs and help us succeed. Their support team is incredibly responsive and always goes above and beyond.",
            author: 'Lisa Wang',
            role: 'Operations Director',
            image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=400',
        },
        image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=800',
    },
    {
        id: 'data-driven',
        name: 'Data-Driven',
        description: 'We believe in the power of data to drive better decisions. Our platform provides comprehensive analytics and insights, helping you understand your audience, optimize your forms, and achieve better results.',
        icon: BarChart3,
        color: 'text-emerald-500',
        principles: ['Provide actionable insights and analytics', 'Help users make data-informed decisions', 'Enable continuous optimization and improvement', 'Measure and demonstrate real business impact'],
        testimonial: {
            quote: "The analytics in Lummeny have completely changed how we approach our marketing campaigns. We can see exactly where people drop off, what works, and what doesn't. It's given us the data we need to make informed decisions.",
            author: 'Michael Brown',
            role: 'Digital Marketing Lead',
            image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=400',
        },
        image: 'https://images.unsplash.com/photo-1536859355448-76f92ebdc33d?q=80&w=800',
    },
]

export default function AboutSectionCompanyValues() {
    const [activeValue, setActiveValue] = useState<string>(companyValues[0].id)

    // Get active value object
    const currentValue = companyValues.find((value) => value.id === activeValue) || companyValues[0]

    return (
        <section className="py-24 pt-32">
            <div className="container mx-auto px-4 md:px-6 2xl:max-w-[1400px]">
                <div className="mx-auto mb-16 max-w-3xl space-y-4 text-center">
                    <h2 className="text-h2 mt-8">What Drives Lummeny Forward</h2>
                    <p className="text-muted-foreground">Our values shape how we build our platform and serve our customers. They guide every decision we make, from product development to customer support.</p>
                </div>

                <Tabs
                    value={activeValue}
                    onValueChange={setActiveValue}
                    className="space-y-8">
                    {/* Value selection - Tabs for md+ screens, Dropdown for smaller screens */}
                    <div className="mb-8 flex justify-center">
                        {/* Tabs for small screens */}
                        <div className="w-full md:hidden">
                            <TabsList className="h-auto w-full p-1">
                                {companyValues.map((value) => (
                                    <TabsTrigger
                                        key={value.id}
                                        value={value.id}
                                        className={cn('flex-1 gap-2')}>
                                        <value.icon className={cn('h-4 w-4', value.color)} />
                                        <span className="hidden sm:inline">{value.name}</span>
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </div>

                        {/* Tabs for medium screens and above */}
                        <TabsList className="border-border hidden h-auto border p-1 md:flex">
                            {companyValues.map((value) => (
                                <TabsTrigger
                                    key={value.id}
                                    value={value.id}
                                    className={cn('h-10 gap-2')}>
                                    <value.icon className={cn('h-6', value.color)} />
                                    <span>{value.name}</span>
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </div>

                    {/* Value content */}
                    <div className="grid items-center gap-8 md:grid-cols-12">
                        {/* Left column: Value details */}
                        <div className="space-y-6 md:col-span-6">
                            <div className="mb-4 flex items-center gap-4">
                                <div className={cn('rounded-xl p-2.5', 'bg-muted')}>
                                    <currentValue.icon className={cn('h-7 w-7', currentValue.color)} />
                                </div>
                                <h3 className="text-2xl font-bold">{currentValue.name}</h3>
                            </div>

                            <p className="text-muted-foreground text-lg">{currentValue.description}</p>

                            <div className="space-y-3 pt-2">
                                <h4 className="font-medium">How We Live This Value:</h4>
                                <ul className="space-y-2">
                                    {currentValue.principles.map((principle, i) => (
                                        <li
                                            key={i}
                                            className="flex items-start gap-2">
                                            <ArrowUpRightIcon className={cn('mt-0.5 h-5 w-5', currentValue.color)} />
                                            <span>{principle}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Right column: Value image */}
                        <div className="md:col-span-6">
                            {currentValue.image ? (
                                <div className="relative aspect-[4/3] overflow-hidden rounded-xl shadow-lg">
                                    <Image
                                        src={currentValue.image}
                                        alt={`Illustration of our ${currentValue.name} value`}
                                        fill
                                        className="object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-6">
                                        <div className={cn('inline-block rounded-lg px-3 py-1 text-sm text-white', 'bg-black/30 backdrop-blur-sm')}>{currentValue.name}</div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-muted flex aspect-[4/3] items-center justify-center rounded-xl">
                                    <currentValue.icon className={cn('h-24 w-24', currentValue.color, 'opacity-25')} />
                                </div>
                            )}
                        </div>
                    </div>
                </Tabs>

                <CallToAction />
            </div>
        </section>
    )
}
